/**
 * WHERE THE SANCTUM BEARER LIVES, and it is ONE place because there is one sign-in in this product.
 *
 * The coordination plane authenticates a PERSON (`hosted.md §6.4`), and the page that asks for the
 * password is `/link.html`. The game does not grow a second form: it sends the player there and comes
 * back, because two sign-in screens for one credential is `arena.md §C` with the identity as its object.
 *
 * `sessionStorage`, on purpose (T1's decision) — and it SURVIVES a same-tab navigation, which is exactly
 * the trip `signInThenReturn` makes.
 */
const BEARER_KEY = 'arena-link-bearer';

export function bearer(): string {
  try {
    return sessionStorage.getItem(BEARER_KEY) ?? '';
  } catch {
    // A tab with storage denied has no bearer, which is the same answer as an empty one.
    return '';
  }
}

export function rememberBearer(token: string): void {
  try {
    sessionStorage.setItem(BEARER_KEY, token);
  } catch {
    // Then it lives for this page only, and the next room asks again. Worse, not broken.
  }
}

export function forgetBearer(): void {
  try {
    sessionStorage.removeItem(BEARER_KEY);
  } catch {
    // Nothing was stored to begin with.
  }
}

/**
 * WHO THIS BROWSER IS ACCORDING TO THE SERVICE — one caller of `/gate/me`, and it is this one.
 *
 * The pass is `HttpOnly`, so the only way a page can learn what it stands for is to ask; and the answer
 * carries three separate facts, which is why the shape is not a boolean: whether this deployment has a
 * lock at all, who is holding the pass, and — when nobody is — WHY (`server/http/gate.ts`).
 *
 * `null` means the question could not be asked. That is not "no lock" and not "no pass": a service that
 * did not answer says nothing about either, and collapsing the three is `code-quality.md §12.2`.
 */
export type GateMe = { status: number; enabled: boolean; identity: { callsign: string; team: { id?: number; tag: string; name: string } | null } | null; error: string };

export async function gateMe(): Promise<GateMe | null> {
  try {
    const res = await fetch('./gate/me', { credentials: 'same-origin', cache: 'no-store' });
    const body = (await res.json().catch(() => ({}))) as Partial<GateMe>;
    return { status: res.status, enabled: body.enabled === true, identity: body.identity ?? null, error: String(body.error ?? '') };
  } catch {
    return null;
  }
}

/**
 * SIGN OUT — and it is TWO things, because a session in this product is held in two places.
 *
 * The bearer is this tab's and script can drop it; the pass is the service's `HttpOnly` cookie and only
 * the service can take it back (`server/http/gate.ts` `POST /gate/out`). Dropping one leaves somebody
 * still holding the game while a screen tells them they left, which is the wrong-screen-that-looks-right
 * of `arena.md §D.8`. Never throws: a service that could not be reached still leaves the tab with
 * nothing, and what the caller does next — the door — is where that is noticed.
 */
export async function signOut(): Promise<void> {
  try {
    await fetch('./gate/out', { method: 'POST', credentials: 'same-origin' });
  } catch {
    // Unreachable, so the cookie stands. The bearer still goes: half of a sign-out that reaches the
    // service later is better than a tab that keeps a credential nobody asked it to hold.
  }
  forgetBearer();
}

/**
 * Send this tab to sign in and bring it back here.
 *
 * `replace` and not `assign`: the trip is plumbing, and leaving it in the history means Back lands on a
 * sign-in page that immediately bounces forward again.
 */
export function signInThenReturn(): void {
  const back = encodeURIComponent(location.href);
  location.replace(`./link.html?next=${back}`);
}
